import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, MapPin, FileCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { formatCurrency } from "./utils";
import { formatDate } from "../../lib/formatters";

const JobHeader = ({
  job,
  canManageJobs,
  effectiveContract,
  totalChangeOrders,
  onBack,
  onStatusChange,
  onDelete,
  onContractReceived,
  onUpdateAddendums,
}) => {
  const navigate = useNavigate();
  const [revertWarningOpen, setRevertWarningOpen] = useState(false);
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [addendumValue, setAddendumValue] = useState(0);

  const handleStatusSelect = (newStatus) => {
    if (newStatus === "bidding" && job.contract_received_date) {
      setRevertWarningOpen(true);
    } else {
      onStatusChange(newStatus);
    }
  };

  const handleConfirmRevert = () => {
    setRevertWarningOpen(false);
    onStatusChange("bidding", { clearContract: true, job });
  };

  const statusClass = {
    bidding: "badge-bidding", precon: "badge-precon", active: "badge-active", complete: "badge-complete",
    archive: "badge-archive", not_bid: "badge-archive", bid_complete: "badge-bid-complete", not_won: "badge-archive",
  }[job.status] || "badge-precon";

  // Show Contract Received button only in Bid Complete status
  const showContractReceivedBtn = job.status === "bid_complete" && canManageJobs;
  
  // Archived statuses where bid sheets should remain unlocked
  const isBiddingArchiveStatus = ["not_bid", "bid_complete", "not_won"].includes(job.status);

  const contractLine = effectiveContract > 0 ? (
    <div className="text-base text-right" data-testid="contract-line">
      <span className="opacity-80">Contract: </span>
      <span className="font-bold">{formatCurrency(effectiveContract)}</span>
      {totalChangeOrders !== 0 && (
        <span className="opacity-80 ml-1">
          (Base: {formatCurrency(job.contract_amount)} + COs: {formatCurrency(totalChangeOrders)})
        </span>
      )}
      {job.contract_received_date && (
        <span className="opacity-80 ml-2">
          (Received: {formatDate(job.contract_received_date)})
        </span>
      )}
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Floating back button — pinned top-left of the content area, in line with the bell */}
      <Button
        variant="outline"
        className="fixed top-3 left-16 md:top-4 md:left-[calc(var(--sidebar-w,16rem)+1.5rem)] z-30 h-9 bg-background/90 backdrop-blur shadow-sm"
        onClick={onBack}
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Jobs
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-2">
            {job.name}
          </h1>
          <p className="text-lg text-muted-foreground">
            {(job.bidding_gcs || []).length > 1 && (job.status === "bidding" || job.status === "bid_complete")
              ? `${job.bidding_gcs.length} GCs Bidding`
              : job.client_name}
          </p>
          {canManageJobs && (
            <div className="flex items-center gap-2 mt-2" data-testid="addendums-row">
              <span className="text-base font-medium text-slate-600">Addendums/Amendments:</span>
              <button
                onClick={() => {
                  setAddendumValue(job.addendums_count || 0);
                  setAddendumDialogOpen(true);
                }}
                className="text-lg font-bold min-w-[2.5rem] px-3 py-0 rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors cursor-pointer ring-2 ring-primary/25"
                title="Click to edit"
                data-testid="addendums-count"
              >
                {job.addendums_count || 0}
              </button>
            </div>
          )}
          {job.address && (
            <div className="flex items-center gap-2 text-muted-foreground mt-2">
              <MapPin className="w-4 h-4" />
              <span>{job.address}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            {job.project_number && (
              <div>
                <span className="text-muted-foreground">Project #: </span>
                <span className="font-medium">{job.project_number}</span>
              </div>
            )}
            {job.contract_number && (
              <div>
                <span className="text-muted-foreground">Contract #: </span>
                <span className="font-medium">{job.contract_number}</span>
              </div>
            )}
            {job.billing_date && (
              <div>
                <span className="text-muted-foreground">Bills on: </span>
                <span className="font-medium">{job.billing_date}{job.billing_date === '1' ? 'st' : job.billing_date === '2' ? 'nd' : job.billing_date === '3' ? 'rd' : 'th'} of each month</span>
              </div>
            )}
          </div>
        </div>

        {canManageJobs && (
          <div className={`flex flex-col items-end gap-2 rounded-lg px-4 py-3 ${statusClass}`} data-testid="status-panel">
            {/* Contract Received Button - above status dropdown */}
            {showContractReceivedBtn && (
              <Button
                variant="default"
                onClick={onContractReceived}
                className="bg-green-600 hover:bg-green-700"
                data-testid="contract-received-btn"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Contract Received
              </Button>
            )}

            <div className="flex items-center gap-2">
              <Select value={job.status} onValueChange={handleStatusSelect}>
                <SelectTrigger className="w-40 bg-white/70 border-current/30 font-semibold text-current" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bidding">Bidding</SelectItem>
                  <SelectItem value="precon">Precon</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="archive">Archive</SelectItem>
                  <SelectItem value="not_bid">Not Bid</SelectItem>
                  <SelectItem value="bid_complete">Bid Complete</SelectItem>
                  <SelectItem value="not_won">Not Won</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" className="w-[60px] bg-white/70 border-current/30 text-current hover:bg-white" onClick={() => navigate(`/jobs/${job.id}/edit`)} data-testid="edit-job-btn">
                <Edit className="w-4 h-4" />
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="icon" data-testid="delete-job-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Job?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      the job and all associated data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onDelete}
                      className="bg-destructive text-destructive-foreground"
                      data-testid="confirm-delete-btn"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {contractLine}
          </div>
        )}
        {!canManageJobs && contractLine && <div className={`flex flex-col items-end rounded-lg px-4 py-3 ${statusClass}`} data-testid="status-panel">{contractLine}</div>}
      </div>
      
      {/* Revert to Bidding Warning */}
      <AlertDialog open={revertWarningOpen} onOpenChange={setRevertWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to Bidding?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the "Contract Received" status and unlock the bid sheets for editing. You will need to accept the contract again after making changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-revert-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRevert}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-revert-btn"
            >
              Revert to Bidding
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Addendums/Amendments Dialog */}
      <Dialog open={addendumDialogOpen} onOpenChange={setAddendumDialogOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Addendums / Amendments</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              type="number"
              min="0"
              value={addendumValue}
              onChange={(e) => setAddendumValue(parseInt(e.target.value) || 0)}
              data-testid="addendums-input"
              className="text-center text-lg"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddendumDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onUpdateAddendums(addendumValue);
                setAddendumDialogOpen(false);
              }}
              data-testid="save-addendums-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobHeader;
